import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';

const FEATURE_NAMES: Record<string, Record<string, string>> = {
  glucophoto:      { en: 'GlucoPhoto',          es: 'GlucoPhoto',           de: 'GlucoPhoto' },
  'weekly-picks':  { en: 'Weekly Picks',         es: 'Picks de la semana',   de: 'Wöchentliche Picks' },
  'weekly-plan':   { en: 'Meal Plan',            es: 'Plan de comidas',      de: 'Mahlzeitenplan' },
  glucofy:         { en: 'Glucofy AI',           es: 'Glucofy IA',           de: 'Glucofy KI' },
};
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { registerFeatureRatingCallback, markFeatureRated, markFeatureSkipped } from '@/hooks/useFeatureRating';

// ─── Manager (rendered once in App.tsx) ───────────────────────────────────────
export function FeatureRatingManager() {
  const [activeFeature, setActiveFeature] = useState<string | null>(null);

  useEffect(() => {
    return registerFeatureRatingCallback(setActiveFeature);
  }, []);

  if (!activeFeature) return null;

  return (
    <FeatureRatingSheet
      feature={activeFeature}
      onClose={() => setActiveFeature(null)}
    />
  );
}

// ─── Sheet ────────────────────────────────────────────────────────────────────
interface Props {
  feature: string;
  onClose: () => void;
}

function FeatureRatingSheet({ feature, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lang = (i18n.language?.substring(0, 2) || 'es') as 'en' | 'es' | 'de';
  const featureDisplayName = FEATURE_NAMES[feature]?.[lang] ?? FEATURE_NAMES[feature]?.['en'] ?? feature;

  const [stars, setStars] = useState(0);
  const [wouldUseAgain, setWouldUseAgain] = useState<boolean | null>(null);
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);

  // Slide-up animation on mount
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleSkip = () => {
    markFeatureSkipped(feature);
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const handleSubmit = async () => {
    if (stars === 0 || !user) return;
    setSaving(true);
    try {
      await supabase.from('feature_ratings' as any).insert({
        user_id: user.id,
        feature,
        rating: stars,
        would_use_again: wouldUseAgain,
        would_recommend: wouldRecommend,
        expected_result: comment.trim() || null,
      });
      markFeatureRated(feature);
    } catch (err) {
      console.error('[FeatureRating] save failed:', err);
    } finally {
      setSaving(false);
      setVisible(false);
      setTimeout(onClose, 300);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={handleSkip}
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9998,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Bottom sheet */}
      <div
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          zIndex: 9999,
          backgroundColor: 'white',
          borderRadius: '20px 20px 0 0',
          padding: '12px 20px 40px',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-out',
        }}
      >
        {/* Handle bar */}
        <div style={{ width: 40, height: 4, backgroundColor: '#E0E0E0', borderRadius: 2, margin: '0 auto 20px' }} />

        {/* Title */}
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1E2820', textAlign: 'center', marginBottom: 22 }}>
          {t('feature_rating.title', { feature: featureDisplayName })}
        </h3>

        {/* Stars */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 24 }}>
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} onClick={() => setStars(s)} style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer' }}>
              <Star
                size={32}
                fill={s <= stars ? '#F59E0B' : 'none'}
                color={s <= stars ? '#F59E0B' : '#D1D5DB'}
                strokeWidth={1.5}
              />
            </button>
          ))}
        </div>

        {/* Would use again */}
        <PillQuestion
          label={t('feature_rating.use_again')}
          value={wouldUseAgain}
          onChange={setWouldUseAgain}
          yesLabel={t('feature_rating.yes')}
          noLabel={t('feature_rating.no')}
        />

        {/* Would recommend */}
        <PillQuestion
          label={t('feature_rating.recommend')}
          value={wouldRecommend}
          onChange={setWouldRecommend}
          yesLabel={t('feature_rating.yes')}
          noLabel={t('feature_rating.no')}
        />

        {/* Comment */}
        <textarea
          placeholder={t('feature_rating.comment_placeholder')}
          maxLength={200}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          style={{
            width: '100%', boxSizing: 'border-box',
            borderRadius: 12, border: '1.5px solid #E0E0E0',
            padding: '10px 12px', fontSize: 14, color: '#374151',
            resize: 'none', outline: 'none',
            fontFamily: 'inherit', marginBottom: 16,
          }}
        />

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={stars === 0 || saving}
          style={{
            width: '100%',
            backgroundColor: stars > 0 ? '#2D5A27' : '#9CA3AF',
            color: 'white',
            borderRadius: 14,
            padding: '14px',
            fontSize: 15,
            fontWeight: 700,
            border: 'none',
            cursor: stars > 0 ? 'pointer' : 'default',
            marginBottom: 4,
            transition: 'background-color 0.15s',
          }}
        >
          {t('feature_rating.submit')}
        </button>

        {/* Skip */}
        <button
          onClick={handleSkip}
          style={{
            width: '100%', padding: '12px',
            fontSize: 14, color: '#6B7280',
            background: 'none', border: 'none', cursor: 'pointer',
          }}
        >
          {t('feature_rating.skip')}
        </button>
      </div>
    </>
  );
}

// ─── Pill yes/no ──────────────────────────────────────────────────────────────
function PillQuestion({
  label, value, onChange, yesLabel, noLabel,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
  yesLabel: string;
  noLabel: string;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>{label}</p>
      <div style={{ display: 'flex', gap: 8 }}>
        {[
          { label: yesLabel, v: true },
          { label: noLabel, v: false },
        ].map(({ label: l, v }) => {
          const active = value === v;
          return (
            <button
              key={String(v)}
              onClick={() => onChange(v)}
              style={{
                padding: '7px 22px',
                borderRadius: 20,
                border: `1.5px solid ${active ? '#2D5A27' : '#E0E0E0'}`,
                backgroundColor: active ? '#2D5A27' : 'white',
                color: active ? 'white' : '#374151',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {l}
            </button>
          );
        })}
      </div>
    </div>
  );
}
